import { useMemo } from 'react'
import { cn } from '@/utils/cn'
import { useTranslation } from '@/i18n'

// Helper functions to replace date-fns
const format = (date: Date, formatStr: string): string => {
  if (formatStr === 'yyyy-MM-dd') {
    return date.toISOString().split('T')[0]
  }
  if (formatStr === 'MMMM yyyy') {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }
  if (formatStr === 'd') {
    return date.getDate().toString()
  }
  return date.toLocaleDateString()
}

const startOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

const endOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0)
}

const eachDayOfInterval = ({ start, end }: { start: Date; end: Date }): Date[] => {
  const days: Date[] = []
  const current = new Date(start)
  while (current <= end) {
    days.push(new Date(current))
    current.setDate(current.getDate() + 1)
  }
  return days
}

const isSameMonth = (date1: Date, date2: Date): boolean => {
  return date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear()
}

const isToday = (date: Date): boolean => {
  const today = new Date()
  return date.toDateString() === today.toDateString()
}

interface StreakCalendarProps {
  checkIns: Date[]
  month?: Date
  className?: string
}

export function StreakCalendar({ checkIns, month = new Date(), className }: StreakCalendarProps) {
  const { t } = useTranslation()
  
  const monthDays = useMemo(() => {
    const start = startOfMonth(month)
    const end = endOfMonth(month)
    return eachDayOfInterval({ start, end })
  }, [month])
  
  const checkInMap = useMemo(() => {
    const map = new Set<string>()
    checkIns.forEach((date: Date) => {
      map.add(format(date, 'yyyy-MM-dd'))
    })
    return map
  }, [checkIns])
  
  const firstDayOfWeek = monthDays[0].getDay()
  const emptyDays = Array.from({ length: firstDayOfWeek }, (_, i) => i)
  
  return (
    <div className={cn('bg-surface rounded-xl border border-border shadow-sm p-xl mb-xl', className)}>
      <h3 className="text-lg font-semibold mb-4">{format(month, 'MMMM yyyy')}</h3>
      
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div key={i} className="text-center text-xs font-medium text-gray-500">
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {emptyDays.map(i => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}
        
        {monthDays.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd')
          const hasCheckIn = checkInMap.has(dateKey)
          const isCurrentDay = isToday(day)
          
          return (
            <div
              key={dateKey}
              className={cn(
                'aspect-square rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                hasCheckIn ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-500 dark:text-gray-400',
                isCurrentDay && 'ring-2 ring-primary ring-offset-1',
                !isSameMonth(day, month) && 'opacity-50'
              )}
            >
              {format(day, 'd')}
            </div>
          )
        })}
      </div>
      
      <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded-sm"></div>
          <span>{t('challenges.completed')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-100 rounded-sm"></div>
          <span>{t('challenges.missed')}</span>
        </div>
      </div>
    </div>
  )
}